// World map data beyond the castles (river, village, building plots) plus the
// movement/elevation engine shared by sim collision and rendering.
//
// Elevation model: the world is a set of walkable LEVELS. Ground is level 0
// everywhere (terrain undulation is purely visual). Boxes flagged `walkable`
// contribute their top as a level (wall-walks, tower tops, the keep roof);
// ramps contribute an interpolated level (stairs). A character may only stand
// where some level lies within STEP_UP of its current elevation — that is
// what stops soldiers walking up the face of a wall, or off the edge of one.

import { castleRamps, castleWalls } from './castle';
import type { Ramp, WallBox } from './castle';
import type { BuildingState, CharacterState, GameState, Team } from './types';

// ── River ────────────────────────────────────────────────────────────────────
// Runs from the north edge down to the plain, west of center, with a bridge
// gap. The south half of the map stays open so armies and the assassin route
// don't need pathfinding.
export const RIVER = { minX: -38, maxX: -26, minZ: 32, maxZ: 150 };
export const BRIDGE = { minZ: 55, maxZ: 63 };

const RIVER_BANKS: WallBox[] = [
  { minX: RIVER.minX, maxX: RIVER.maxX, minZ: RIVER.minZ, maxZ: BRIDGE.minZ, height: 2.5 },
  { minX: RIVER.minX, maxX: RIVER.maxX, minZ: BRIDGE.maxZ, maxZ: 150, height: 2.5 },
];

// ── Village ──────────────────────────────────────────────────────────────────
// A cluster of houses south-east of center. Houses block movement.
export interface House {
  box: WallBox;
  /** roof ridge runs along X (true) or Z (false) — purely visual */
  alongX: boolean;
}

export const VILLAGE_CENTER = { x: 25, z: -50 };

export const HOUSES: House[] = [
  { box: { minX: 14, maxX: 22, minZ: -46, maxZ: -40, height: 3.2 }, alongX: true },
  { box: { minX: 27, maxX: 34, minZ: -44, maxZ: -38, height: 3.0 }, alongX: false },
  { box: { minX: 12, maxX: 18, minZ: -58, maxZ: -52, height: 3.0 }, alongX: false },
  { box: { minX: 24, maxX: 33, minZ: -62, maxZ: -55, height: 3.4 }, alongX: true },
  { box: { minX: 36, maxX: 42, minZ: -54, maxZ: -48, height: 2.8 }, alongX: true },
  // barn on the village edge
  { box: { minX: 44, maxX: 54, minZ: -66, maxZ: -59, height: 4.2 }, alongX: true },
];

// ── Building plots ───────────────────────────────────────────────────────────
// Economy buildings go on fixed plots in each castle's hinterland, outside
// the walls (which is what makes them raidable).
export interface Plot {
  x: number;
  z: number;
}

export function buildingPlots(team: Team): Plot[] {
  const s = team === 'friendly' ? -1 : 1;
  return [
    { x: s * 150, z: 26 },
    { x: s * 150, z: 36 },
    { x: s * 157, z: 48 },
    { x: s * 148, z: -26 },
    { x: s * 148, z: -36 },
    { x: s * 157, z: -48 },
  ];
}

/** Bailey plots inside the walls — home of the smithy, range, chapel,
 * treasury, and housing. Enterable and only lost if the walls are breached. */
export function baileyPlots(team: Team): Plot[] {
  const s = team === 'friendly' ? -1 : 1;
  return [
    { x: s * 171, z: 30 },
    { x: s * 171, z: -30 },
    { x: s * 186, z: 46 },
    { x: s * 186, z: -46 },
    { x: s * 177, z: -36 },
  ];
}

export function plotOf(team: Team, b: { plot: 'field' | 'bailey'; plotIndex: number }): Plot {
  return (b.plot === 'bailey' ? baileyPlots(team) : buildingPlots(team))[b.plotIndex];
}

// ── dynamic obstacles: completed bailey buildings block movement ─────────────
// Four wall slabs with a door gap facing the courtyard center, so buildings
// are solid but enterable. Rebuilt only when the building list changes.

const BAILEY_HALF = 3;
const BAILEY_WALL_H = 3.5;

let dynamicObstacles: WallBox[] = [];
let dynamicSignature = '__none__';
let allWithDynamic: WallBox[] | null = null;

/** Static structures plus completed bailey buildings. */
function obstacles(): WallBox[] {
  return allWithDynamic ?? ALL_OBSTACLES;
}

function buildingWalls(b: BuildingState): WallBox[] {
  const p = plotOf(b.team, b);
  const t = 0.35;
  const minX = p.x - BAILEY_HALF;
  const maxX = p.x + BAILEY_HALF;
  const minZ = p.z - BAILEY_HALF;
  const maxZ = p.z + BAILEY_HALF;
  const doorOnMinZ = p.z > 0; // door faces the courtyard center (z toward 0)
  const doorZWall = doorOnMinZ
    ? [
        { minX, maxX: p.x - 1.1, minZ, maxZ: minZ + t, height: BAILEY_WALL_H },
        { minX: p.x + 1.1, maxX, minZ, maxZ: minZ + t, height: BAILEY_WALL_H },
        { minX, maxX, minZ: maxZ - t, maxZ, height: BAILEY_WALL_H },
      ]
    : [
        { minX, maxX: p.x - 1.1, minZ: maxZ - t, maxZ, height: BAILEY_WALL_H },
        { minX: p.x + 1.1, maxX, minZ: maxZ - t, maxZ, height: BAILEY_WALL_H },
        { minX, maxX, minZ, maxZ: minZ + t, height: BAILEY_WALL_H },
      ];
  return [
    ...doorZWall,
    { minX, maxX: minX + t, minZ: minZ + t, maxZ: maxZ - t, height: BAILEY_WALL_H },
    { minX: maxX - t, maxX, minZ: minZ + t, maxZ: maxZ - t, height: BAILEY_WALL_H },
  ];
}

/** Call once per tick (and before rendering): keeps collision in sync with
 * the building list. Purely derived from state, so replay-safe. */
export function syncBuildingObstacles(state: GameState): void {
  const signature = state.buildings
    .map((b) => `${b.id}:${b.progress >= 1 ? 1 : 0}`)
    .join(',');
  if (signature === dynamicSignature) return;
  dynamicSignature = signature;
  dynamicObstacles = [];
  for (const b of state.buildings) {
    if (b.plot !== 'bailey' || b.progress < 1) continue;
    dynamicObstacles.push(...buildingWalls(b));
  }
  allWithDynamic = [...ALL_OBSTACLES, ...dynamicObstacles];
}

// ── Combined structure lists ─────────────────────────────────────────────────

const ALL_OBSTACLES: WallBox[] = [
  ...castleWalls('friendly'),
  ...castleWalls('enemy'),
  ...RIVER_BANKS,
  ...HOUSES.map((h) => h.box),
];

const ALL_RAMPS: Ramp[] = [...castleRamps('friendly'), ...castleRamps('enemy')];

const BODY_RADIUS = 0.4;
/** How tall a step a character can take up or down. */
export const STEP_UP = 1.5;
/** Character body height, for passing under lintels and arches. */
const CHAR_H = 1.7;

function rampHeightAt(r: Ramp, x: number, z: number): number {
  const t =
    r.axis === 'x' ? (x - r.minX) / (r.maxX - r.minX) : (z - r.minZ) / (r.maxZ - r.minZ);
  return r.h0 + (r.h1 - r.h0) * t;
}

const levelsScratch: number[] = [];

/** Collect every standable level at (x, z) into `out`. Ground (0) is included
 * unless a from-the-ground solid or a high stair blocks it. */
export function levelsAt(x: number, z: number, out: number[]): void {
  out.length = 0;
  let groundOpen = true;
  for (const w of obstacles()) {
    if (x <= w.minX || x >= w.maxX || z <= w.minZ || z >= w.maxZ) continue;
    if (w.walkable) out.push(w.height);
    if ((w.minY ?? 0) < 1.2 && w.height > STEP_UP) groundOpen = false;
  }
  for (const r of ALL_RAMPS) {
    if (x <= r.minX || x >= r.maxX || z <= r.minZ || z >= r.maxZ) continue;
    const h = rampHeightAt(r, x, z);
    out.push(h);
    if (h > 1.6) groundOpen = false; // stairs are solid masonry beneath
  }
  if (groundOpen) out.push(0);
}

/** The highest level at (x, z) reachable (±STEP_UP) from elevation `elev`,
 * or null if none — meaning a character at `elev` cannot stand there. */
export function reachableLevel(x: number, z: number, elev: number): number | null {
  levelsAt(x, z, levelsScratch);
  let best: number | null = null;
  for (const l of levelsScratch) {
    if (Math.abs(l - elev) > STEP_UP) continue;
    if (best === null || l > best) best = l;
  }
  return best;
}

/** Settle a character after it moved from (fromX, fromZ): keep the move if a
 * reachable level exists, otherwise slide along one axis, otherwise revert.
 * Updates c.elev to the level it lands on. */
export function settleMove(c: CharacterState, fromX: number, fromZ: number): void {
  let level = reachableLevel(c.x, c.z, c.elev);
  if (level === null) {
    const newX = c.x;
    const newZ = c.z;
    level = reachableLevel(newX, fromZ, c.elev);
    if (level !== null) {
      c.z = fromZ;
    } else {
      level = reachableLevel(fromX, newZ, c.elev);
      if (level !== null) {
        c.x = fromX;
      } else {
        c.x = fromX;
        c.z = fromZ;
        level = reachableLevel(fromX, fromZ, c.elev);
      }
    }
  }
  if (level !== null) c.elev = level;
}

/** After separation/collision nudges, re-validate a character's footing; an
 * invalid spot (e.g. pushed off a wall edge) reverts to its last safe one. */
export function enforceLevel(c: CharacterState): void {
  const level = reachableLevel(c.x, c.z, c.elev);
  if (level !== null) {
    c.elev = level;
    c.safeX = c.x;
    c.safeZ = c.z;
    return;
  }
  c.x = c.safeX;
  c.z = c.safeZ;
  const safe = reachableLevel(c.x, c.z, c.elev);
  if (safe !== null) c.elev = safe;
}

/** True when box `w` blocks a character at elevation `elev`: it overlaps the
 * character's body vertically and its top is too high to step onto. */
function boxBlocks(w: WallBox, elev: number): boolean {
  if (elev + CHAR_H <= (w.minY ?? 0)) return false; // passes beneath (arches)
  if (w.height - elev <= STEP_UP) return false; // standing on it / step onto it
  return true;
}

/** Push a character out of any obstacle it overlaps (cheapest-axis). */
export function resolveObstacles(c: CharacterState): void {
  for (const w of obstacles()) {
    if (
      c.x <= w.minX - BODY_RADIUS ||
      c.x >= w.maxX + BODY_RADIUS ||
      c.z <= w.minZ - BODY_RADIUS ||
      c.z >= w.maxZ + BODY_RADIUS
    ) {
      continue;
    }
    if (!boxBlocks(w, c.elev)) continue;
    const pushWest = c.x - (w.minX - BODY_RADIUS);
    const pushEast = w.maxX + BODY_RADIUS - c.x;
    const pushSouth = c.z - (w.minZ - BODY_RADIUS);
    const pushNorth = w.maxZ + BODY_RADIUS - c.z;
    const min = Math.min(pushWest, pushEast, pushSouth, pushNorth);
    if (min === pushWest) c.x = w.minX - BODY_RADIUS;
    else if (min === pushEast) c.x = w.maxX + BODY_RADIUS;
    else if (min === pushSouth) c.z = w.minZ - BODY_RADIUS;
    else c.z = w.maxZ + BODY_RADIUS;
  }
}

/** All collision boxes (renderer uses these for camera clipping). */
export function obstacleList(): WallBox[] {
  return obstacles();
}

/** All stairs (renderer builds step meshes from these). */
export function rampList(): Ramp[] {
  return ALL_RAMPS;
}

/** True when a point in 3D space is inside solid masonry (arrow impacts). */
export function arrowBlockedAt(x: number, z: number, y: number): boolean {
  for (const w of obstacles()) {
    if (x <= w.minX || x >= w.maxX || z <= w.minZ || z >= w.maxZ) continue;
    if (y > (w.minY ?? 0) && y < w.height) return true;
  }
  for (const r of ALL_RAMPS) {
    if (x <= r.minX || x >= r.maxX || z <= r.minZ || z >= r.maxZ) continue;
    if (y < rampHeightAt(r, x, z)) return true;
  }
  return false;
}
